import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  AlertTriangle,
  ShieldCheck,
  ShieldOff,
  RefreshCw,
  Bell,
  Ban,
  Trash2,
  ArrowUpRight,
  CheckCircle,
} from "lucide-react";
import api from "../../utils/api";

const statusOptions = [
  { value: "pending", label: "Pending", tone: "text-amber-700 bg-amber-50" },
  { value: "reviewed", label: "Reviewed", tone: "text-blue-700 bg-blue-50" },
  {
    value: "actioned",
    label: "Actioned",
    tone: "text-emerald-700 bg-emerald-50",
  },
];

const ModerationQueue = () => {
  const [queue, setQueue] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const loadQueue = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(
        `/moderation/queue?status=${statusFilter}`,
      );
      setQueue(data?.data || data || []);
    } catch (error) {
      console.error("Failed to load moderation queue", error);
      toast.error("Failed to load moderation queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQueue();
  }, [statusFilter]);

  const makeActionKey = (itemId, action) => `${action}-${itemId}`;

  const runAction = async (item, action) => {
    console.log("[POST DATA]", item);
    const postId = item.postId;
    const flagId = item.id || item._id;
    const userId =
      item.userId ||
      item.authorId ||
      item.author?._id ||
      item.author?.id ||
      item.author;
    if (
      (action === "warn" || action === "suspend" || action === "unsuspend") &&
      !userId
    ) {
      toast.error(`Cannot ${action}: author ID not found in post data`);
      return;
    }
    if (
      (action === "remove" || action === "escalate" || action === "dismiss") &&
      !postId
    ) {
      toast.error("Post id missing for this action");
      return;
    }
    const actionKey = makeActionKey(postId || flagId || userId, action);

    setActionLoading((prev) => ({ ...prev, [actionKey]: true }));
    try {
      switch (action) {
        case "warn":
          await api.post(`/moderation/actions/warn/${userId}`);
          toast.success("User warned");
          break;
        case "suspend":
          await api.post(`/moderation/actions/suspend/${userId}`);
          toast.success("User suspended");
          break;
        case "remove":
          await api.delete(`/moderation/actions/post/${postId}`);
          toast.success("Post removed");
          break;
        case "escalate":
          await api.post(`/moderation/actions/escalate/${postId}`);
          toast.success("Post escalated");
          break;
        case "dismiss":
          await api.post(`/moderation/queue/${flagId}/dismiss`);
          toast.success("Flag dismissed");
          break;
        case "unsuspend":
          if (!window.confirm("Restore posting privileges for this user?"))
            return;
          await api.post(`/moderation/actions/unsuspend/${userId}`);
          toast.success("User unsuspended successfully");
          break;
        default:
          break;
      }
      loadQueue();
    } catch (error) {
      console.error(`Failed to ${action}`, error);
      toast.error(error.response?.data?.message || `Failed to ${action}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [actionKey]: false }));
    }
  };

  const summary = useMemo(() => {
    const total = queue.length;
    const flaggedUsers = new Set(
      queue.map((item) => item.userId || item.authorId),
    ).size;
    return { total, flaggedUsers };
  }, [queue]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#1a3884]" />
          <div>
            <p className="text-sm text-gray-500">Moderation Queue</p>
            <p className="text-lg font-semibold text-gray-900">
              {summary.total} flagged items
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1a3884] focus:outline-none"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            onClick={loadQueue}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1a3884] mx-auto" />
          <p className="mt-3 text-gray-500">Loading moderation items...</p>
        </div>
      ) : queue.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="font-medium text-gray-900">No items in this queue</p>
          <p className="text-sm text-gray-500">All clear for now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {queue.map((item) => {
            const badge = statusOptions.find(
              (opt) => opt.value === (item.status || statusFilter),
            );
            const actionKeyBase = item.postId || item._id || item.id;

            return (
              <div
                key={actionKeyBase}
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-500">Flag reason</p>
                    <p className="font-semibold text-gray-900">
                      {item.flagReason || "Unspecified"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Flagged{" "}
                      {item.flaggedAt
                        ? new Date(item.flaggedAt).toLocaleString()
                        : "recently"}
                      {item.reporter && ` • Reporter: \${item.reporter}`}
                    </p>
                  </div>
                  {badge && (
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold \${badge.tone}`}
                    >
                      {badge.label}
                    </span>
                  )}
                </div>

                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-100 max-h-40 overflow-y-auto whitespace-pre-wrap">
                  {item.preview || item.content || "No preview available"}
                </div>

                <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
                  <Bell size={14} />
                  <span>
                    Author:{" "}
                    {item.authorName ||
                      item.author?.fullName ||
                      item.author?.name ||
                      "Unknown"}
                  </span>
                  {(item.author?.status === "suspended" ||
                    item.author?.suspendedUntil) && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold">
                      SUSPENDED
                    </span>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <button
                    onClick={() => runAction(item, "warn")}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50"
                    disabled={
                      actionLoading[makeActionKey(actionKeyBase, "warn")]
                    }
                  >
                    <Bell size={14} /> Warn
                  </button>
                  <button
                    onClick={() => runAction(item, "suspend")}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold border border-red-200 text-red-700 rounded-lg hover:bg-red-50"
                    disabled={
                      actionLoading[makeActionKey(actionKeyBase, "suspend")]
                    }
                  >
                    <Ban size={14} /> Suspend
                  </button>
                  <button
                    onClick={() => runAction(item, "remove")}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                    disabled={
                      actionLoading[makeActionKey(actionKeyBase, "remove")]
                    }
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                  <button
                    onClick={() => runAction(item, "escalate")}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold border border-[#1a3884]/20 text-[#1a3884] rounded-lg hover:bg-[#1a3884]/10"
                    disabled={
                      actionLoading[makeActionKey(actionKeyBase, "escalate")]
                    }
                  >
                    <ArrowUpRight size={14} /> Escalate
                  </button>
                  <button
                    onClick={() => runAction(item, "dismiss")}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50"
                    disabled={
                      actionLoading[makeActionKey(actionKeyBase, "dismiss")]
                    }
                  >
                    <CheckCircle size={14} /> Dismiss
                  </button>
                  {(item.author?.status === "suspended" ||
                    item.author?.suspendedUntil) && (
                    <button
                      onClick={() => runAction(item, "unsuspend")}
                      className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold border border-green-300 text-green-700 bg-green-50 rounded-lg hover:bg-green-100"
                      disabled={
                        actionLoading[makeActionKey(actionKeyBase, "unsuspend")]
                      }
                    >
                      <ShieldOff size={14} /> Unsuspend
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ModerationQueue;
